import { useState, useRef, useMemo, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import { ARTISTS } from "@/data/artists";
import { buildTracklist } from "@/lib/tracks";

const AUDIO_BASE = "/samples/musical-collection";

/**
 * Artist deep-page — /artists/:slug
 * Works for any artist by slug lookup.
 * Nordlys (id 34) gets the first cinematic hero surface.
 */
export default function ArtistDetail() {
  const { slug } = useParams();
  const artist = useMemo(() => ARTISTS.find((a) => a.slug === slug), [slug]);
  const [playingSlug, setPlayingSlug] = useState(null);
  const audioRef = useRef(null);
  // 2026-08-29 Founder plain-ink — fetch the on-disk mp3 manifest so the
  // deep-page tracklist filters to actually-playable tracks and togglePlay
  // refuses to set audio.src on files that don't exist yet. Prevents the
  // MEDIA_ERR_SRC_NOT_SUPPORTED wedge that made most band pages silent.
  const [availableSet, setAvailableSet] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${AUDIO_BASE}/manifest.json`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && Array.isArray(d.slugs)) {
          setAvailableSet(new Set(d.slugs));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!artist) {
    return <Navigate to="/artists" replace />;
  }

  const isPlayable = (slugStr) => !availableSet || availableSet.has(slugStr);

  const togglePlay = (trackSlug) => {
    if (playingSlug === trackSlug) {
      audioRef.current?.pause();
      setPlayingSlug(null);
      return;
    }
    // Manifest guard — silently no-op if the mp3 isn't on disk yet.
    if (!isPlayable(trackSlug)) return;
    const src = `${AUDIO_BASE}/${trackSlug}.mp3`;
    if (audioRef.current) {
      audioRef.current.src = src;
      audioRef.current.play().catch(() => setPlayingSlug(null));
    }
    setPlayingSlug(trackSlug);
  };

  // Founder plain-ink 2026-02-15: full album per band, not the featured 4.
  const tracks = buildTracklist(artist, availableSet);
  const heroTrack =
    artist.heroTrack && isPlayable(artist.heroTrack.slug)
      ? artist.heroTrack
      : tracks[0] || null;

  return (
    <Layout>
      {/* HERO — cinematic accent-tinted */}
      <section
        data-testid="artist-detail-hero"
        className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 border-b border-white/[0.05]"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1400px] h-[800px] opacity-[0.15] blur-3xl"
            style={{ background: `radial-gradient(circle at 50% 0%, ${artist.accent}, transparent 60%)` }}
          />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black to-transparent" />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 sm:px-10">
          <Link
            to="/artists"
            data-testid="artist-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#a1a1aa] hover:text-white transition"
          >
            ← Back to the roster
          </Link>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-10 items-end">
            <div>
              <p
                className="font-mono text-[11px] uppercase tracking-[0.45em]"
                style={{ color: artist.accent }}
                data-testid="artist-lane"
              >
                {artist.lane}
              </p>
              <h1
                data-testid="artist-name"
                className="font-display uppercase leading-[0.9] mt-6 text-[#ededed]"
                style={{ fontSize: "clamp(3rem, 10vw, 8rem)", letterSpacing: "-0.01em" }}
              >
                {artist.name}
              </h1>
              <p
                data-testid="artist-tagline"
                className="font-display italic text-lg sm:text-2xl text-[#d4d4d8] mt-8 max-w-2xl leading-relaxed"
              >
                {artist.tagline}
              </p>
            </div>

            <div className="flex flex-col gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a]">
              <div>
                <span className="text-[#a1a1aa]">{String(artist.id).padStart(2, "0")}</span> / 37
              </div>
              <div>
                <span className="text-[#a1a1aa]">{availableSet ? tracks.length : (artist.trackCount || 0)}</span> tracks
              </div>
              {artist.reference && (
                <div className="max-w-[240px] text-right lg:text-left leading-relaxed normal-case tracking-normal font-sans text-[#71717a] text-[11px] mt-2">
                  Reference lane: {artist.reference}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* DEEP-PAGE PORTRAIT — Founder plain-ink 2026-02-15.
          A second photograph of the same artist/ensemble sits under the
          hero band. Same person, different frame. If the JPEG isn't on
          disk yet, the img.onError handler quietly hides the block so we
          never render a broken frame. */}
      <DeepPortrait artist={artist} />

      {/* LORE / VOICE / PALETTE — three-column deep spec */}
      {(artist.lore || artist.voiceRecipe || artist.palette) && (
        <section
          data-testid="artist-spec"
          className="relative py-16 sm:py-24 border-b border-white/[0.05]"
        >
          <div className="max-w-[1200px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-3 gap-10 sm:gap-14">
            {artist.lore && (
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.4em] mb-4"
                  style={{ color: artist.accent }}
                >
                  Lore
                </p>
                <p className="font-display italic text-lg sm:text-xl text-[#d4d4d8] leading-relaxed">
                  {artist.lore}
                </p>
              </div>
            )}
            {artist.voiceRecipe && (
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.4em] mb-4"
                  style={{ color: artist.accent }}
                >
                  Voice recipe
                </p>
                <p className="text-[#d4d4d8] leading-relaxed">
                  {artist.voiceRecipe}
                </p>
              </div>
            )}
            {artist.palette && (
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-[0.4em] mb-4"
                  style={{ color: artist.accent }}
                >
                  Visual palette
                </p>
                <p className="text-[#d4d4d8] leading-relaxed">
                  {artist.palette}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* TRACKLIST */}
      <section
        data-testid="artist-tracklist"
        className="relative py-16 sm:py-24"
      >
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10">
          <div className="flex items-baseline justify-between mb-10">
            <p
              className="font-mono text-[10px] uppercase tracking-[0.4em]"
              style={{ color: artist.accent }}
            >
              The tracklist
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a]">
              {tracks.length} songs · press play
            </p>
          </div>

          {tracks.length === 0 ? (
            <p className="font-display italic text-lg text-[#71717a]">
              Track menu queued for bake — check back when the tape rolls.
            </p>
          ) : (
            <ol className="space-y-2">
              {tracks.map((t, i) => {
                const active = playingSlug === t.slug;
                return (
                  <motion.li
                    key={t.slug}
                    data-testid={`artist-track-${t.slug}`}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03, duration: 0.35 }}
                  >
                    <button
                      type="button"
                      onClick={() => togglePlay(t.slug)}
                      className="w-full group flex items-center gap-4 sm:gap-6 py-4 px-4 sm:px-6 -mx-4 sm:-mx-6 rounded-md border border-transparent hover:border-white/10 hover:bg-white/[0.03] transition-colors text-left"
                      style={active ? { borderColor: `${artist.accent}55`, background: `${artist.accent}0d` } : undefined}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] w-10 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="w-8 h-8 rounded-full grid place-items-center shrink-0 transition-all"
                        style={{
                          background: active ? artist.accent : "rgba(255,255,255,0.04)",
                          border: `1px solid ${active ? artist.accent : "rgba(255,255,255,0.1)"}`,
                        }}
                      >
                        <span className="text-black text-xs" style={{ opacity: active ? 1 : 0 }}>
                          ▶
                        </span>
                        <span
                          className="absolute text-white text-xs"
                          style={{ opacity: active ? 0 : 0.9 }}
                        >
                          ▷
                        </span>
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block font-display text-lg sm:text-2xl text-[#ededed] leading-tight truncate">
                          {t.title}
                        </span>
                        {t.tag && (
                          <span className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[#71717a] mt-1">
                            {t.tag}
                          </span>
                        )}
                      </span>
                      <span
                        className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.28em] text-[#52525b] shrink-0"
                        style={active ? { color: artist.accent } : undefined}
                      >
                        {active ? "playing" : "▷"}
                      </span>
                    </button>
                  </motion.li>
                );
              })}
            </ol>
          )}
        </div>
      </section>

      {/* HERO CTA / FOOTER STRIP */}
      {heroTrack && (
        <section className="relative py-14 border-t border-white/[0.05]">
          <div className="max-w-[1000px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
            <p className="font-display italic text-lg sm:text-2xl text-[#d4d4d8]">
              Now cued · <span style={{ color: artist.accent }}>{heroTrack.title}</span>
            </p>
            <Link
              to="/artists"
              className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#a1a1aa] hover:text-white transition"
              data-testid="artist-detail-back"
            >
              Back to the roster ↗
            </Link>
          </div>
        </section>
      )}

      <audio
        ref={audioRef}
        data-testid="artist-detail-audio"
        onEnded={() => setPlayingSlug(null)}
      />
    </Layout>
  );
}

// DeepPortrait — second photograph per band, fits the band's canon.
// Baked via /app/backend/gen_artist_deep_portraits.py into
// /artists/portraits/deep/{slug}.jpg. If the file isn't on disk yet the
// block auto-hides. Founder plain-ink 2026-02-15.
function DeepPortrait({ artist }) {
  const [broken, setBroken] = useState(false);
  if (broken) return null;
  const src = `/artists/portraits/deep/${artist.slug}.jpg`;
  return (
    <section
      data-testid={`artist-deep-portrait-${artist.slug}`}
      className="relative border-b border-white/[0.05]"
    >
      <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <div className="relative overflow-hidden">
          <img
            src={src}
            alt={`${artist.name} — deep-page portrait`}
            className="w-full h-auto max-h-[720px] object-cover object-top"
            loading="lazy"
            onError={() => setBroken(true)}
            style={{
              boxShadow: `0 40px 120px -20px ${artist.accent}44`,
            }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
            style={{
              background:
                "linear-gradient(to top, rgba(5,5,5,0.85), transparent)",
            }}
          />
          <p
            className="absolute bottom-4 left-6 font-mono text-[10px] uppercase tracking-[0.32em]"
            style={{ color: artist.accent }}
          >
            {artist.name} · frame two
          </p>
        </div>
      </div>
    </section>
  );
}
